import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'
import { format } from 'date-fns'

import { PassengerIcon } from '@dfds-frontend/navigator-icons/icons'

import { isSogRoute } from '../../Utils/sog-helpers'

import { useRoutes } from '../../Hooks/routes/useRoutes'
import { useVehicles } from '../../Hooks/vehicles/useVehicles'

import { ActiveSelectField, useActiveSelectField } from '../../context/ActiveSelectFieldProvider'
import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import SelectField from '../shared/SelectField'
import Passengers from './Passengers'

const PassengersSelector = () => {
  const passengerTriggerRef = useRef<HTMLButtonElement>(null)
  const restoreTriggerFocusRef = useRef(false)
  const { state } = useTravelSearchContext()
  const { isRoutesLoading, selectedRoute } = useRoutes()
  const { isVehiclesLoading, numberOfPassengers, selectedPassengers } = useVehicles()
  const { activeField, closeIfActive, toggleField } = useActiveSelectField()
  const { t } = useTranslation()

  const isOpen = activeField === ActiveSelectField.PASSENGER

  useEffect(() => {
    if (isOpen || !restoreTriggerFocusRef.current) return
    restoreTriggerFocusRef.current = false
    passengerTriggerRef.current?.focus()
  }, [isOpen])

  const routeCode = selectedRoute?.routeCode ?? ''
  const outboundDate = state.selectedDates?.outboundDate ? format(state.selectedDates.outboundDate, 'yyyy-MM-dd') : undefined
  const isHidden = isSogRoute(routeCode, outboundDate)

  const dataAttrList = [
    { name: 'selected-adults', value: `${selectedPassengers?.adults}` },
    { name: 'selected-children', value: `${selectedPassengers?.children}` },
    {
      name: 'child-ages',
      value: `${selectedPassengers?.childrenAges?.join(',')}`,
    },
    { name: 'selected-pets', value: `${selectedPassengers?.pets}` },
  ]

  const displayValue =
    selectedPassengers?.pets && selectedPassengers?.pets > 0 ? `${numberOfPassengers} + ${t('PETS')}` : `${numberOfPassengers}`

  return (
    <div className={clsx('col-span-12 box-border h-[58px] w-full md:col-span-6 lg:flex-1', isHidden && 'hidden')}>
      <SelectField
        className="lg:border-r-0"
        dataAttrList={dataAttrList}
        fieldName="passenger"
        icon={<PassengerIcon size="md" />}
        isLoading={isRoutesLoading || isVehiclesLoading}
        isOpen={isOpen}
        onClick={(clickedOutside) => {
          !isRoutesLoading && !isVehiclesLoading && toggleField(ActiveSelectField.PASSENGER, clickedOutside)
        }}
        onClose={() => closeIfActive(ActiveSelectField.PASSENGER)}
        title={`${t('PASSENGERS')} & ${t('PETS')}`}
        triggerRef={passengerTriggerRef}
        value={displayValue}
        visibleRows={selectedPassengers?.adults && selectedPassengers.pets ? 2 : 1}
      >
        {selectedPassengers ? (
          <Passengers
            initialPassengers={selectedPassengers}
            onEscape={() => {
              restoreTriggerFocusRef.current = true
              closeIfActive(ActiveSelectField.PASSENGER)
            }}
            onFocusLeave={() => closeIfActive(ActiveSelectField.PASSENGER)}
            triggerRef={passengerTriggerRef}
          />
        ) : null}
      </SelectField>
    </div>
  )
}

export default PassengersSelector
